import type { DeliveryData } from '@consumer/services/order'
import type DateStr from '@consumer/types/DateStr'
import type DateTimeStr from '@consumer/types/DateTimeStr'
import {
  addDays,
  addMonths,
  compareAsc,
  format as dateFnsFormat,
  isPast as dateFnsIsPast,
  isToday as dateFnsIsToday,
  parseJSON as dateFnsParseJSON,
  parseISO as dateFnsParseISO,
  parse,
  subDays,
  subMonths,
} from 'date-fns'
import { formatInTimeZone as dateFnsFormatInTimezone } from 'date-fns-tz'

export {
  addDays, addMonths, subDays, subMonths,
}

// Default year for optional year handling, leap year to allow February 29
export const DEFAULT_YEAR_FOR_OPTIONAL_YEAR = 1200
export const DEFAULT_SELECTED_YEAR_FOR_BIRTHDAY = 2000

export const DEFAULT_LONG_DATE_FORMAT = 'E, MMM d, yyyy'
export const DEFAULT_MEDIUM_DATE_FORMAT = 'MMM d, yyyy'
export const DEFAULT_FULL_MONTH_DATE_FORMAT = 'MMMM d, yyyy'
export const DEFAULT_SHORT_DATE_FORMAT = 'M/d'
export const ISO_FORMAT = 'yyyy-MM-dd'

interface FormatDateOptions {
  format: string
  showNow?: boolean
  timezone?: string
}

export function isDateStr (dateStr: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
}

export function formatDate (
  date: DateTimeStr | DateStr | number | null | undefined,
  {
    format,
    showNow,
    timezone,
  }: FormatDateOptions = {
    format: DEFAULT_LONG_DATE_FORMAT,
  },
): string {
  try {
    if (!date) {
      return ''
    }

    let dateToFormat = date
    if (typeof dateToFormat === 'string') {
      let format = null
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateToFormat)) {
        format = ISO_FORMAT
      }
      dateToFormat = parseDate(dateToFormat, format)
    }

    if (showNow && isToday(dateToFormat)) {
      return 'Now'
    }
    else {
      if (timezone) {
        return dateFnsFormatInTimezone(dateToFormat, timezone, format)
      }
      else {
        return dateFnsFormat(dateToFormat, format)
      }
    }
  }
  catch (e) {
    return `Error: ${e} - ${date}`
  }
}

export function parseDbDate (date: DateTimeStr | DateStr) {
  return parseDate(date, 'yyyy-MM-dd')
}

export function parseDate (date: string | Date, format?: string | null) {
  if (date instanceof Date) {
    return date
  }
  else if (format) {
    return parse(date, format, new Date())
  }
  else {
    return dateFnsParseJSON(date)
  }
}

export function isToday (date: Date | string | number | undefined | null) {
  if (typeof date === 'string') {
    const parsedJSONDate = dateFnsParseJSON(date)
    if (!isNaN(parsedJSONDate.valueOf())) {
      date = parsedJSONDate
    }
    else if (isDateStr(String(date))) {
      date = parseDate(date, 'yyyy-MM-dd')
    }
    else {
      return date === 'Today' || date === 'Now'
    }
  }

  if (date) {
    return dateFnsIsToday(date)
  }
  else {
    return false
  }
}

export function isTomorrow (date: Date | string | number | undefined | null) {
  let parsedDate: Date | null = null

  if (typeof date === 'string') {
    const parsedJSONDate = dateFnsParseJSON(date)
    if (!isNaN(parsedJSONDate.valueOf())) {
      parsedDate = parsedJSONDate
    }
    else if (isDateStr(String(date))) {
      parsedDate = parseDate(date, 'yyyy-MM-dd')
    }
  }
  else if (date instanceof Date) {
    parsedDate = date
  }
  else if (typeof date === 'number') {
    parsedDate = new Date(date)
  }

  if (parsedDate) {
    return dateFnsIsToday(addDays(parsedDate, -1))
  }
  else {
    return false
  }
}

export function isBeforeNow (date: Date | string | number | undefined | null) {
  const dateParsed = typeof date === 'string' ? dateFnsParseISO(date) : date

  if (!dateParsed) return true

  return dateFnsIsPast(dateParsed)
}

export function isPast (date: Date | string | number | undefined | null) {
  if (typeof date === 'string') {
    const parsedJSONDate = dateFnsParseJSON(date)
    if (!isNaN(parsedJSONDate.valueOf())) {
      date = parsedJSONDate
    }
    else if (isDateStr(String(date))) {
      date = parseDate(date, 'yyyy-MM-dd')
    }
    else {
      return false
    }
  }

  if (date) {
    return dateFnsIsPast(date)
  }
  else {
    return false
  }
}

export const isYesterday = (date: Date) => compareAsc(date, new Date()) === -1

export function isBeforeToday (date: Date | string): boolean {
  const eventDate = typeof date === 'string' ? parseDate(date, 'yyyy-MM-dd') : new Date(date)
  const today = new Date()
  eventDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  return eventDate < today
}

export function formatDaysFromNow (date: Date | string): string {
  const eventDate = typeof date === 'string' ? parseDate(date, 'yyyy-MM-dd') : new Date(date)
  const today = new Date()
  eventDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  const diffTime = eventDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 1) return '1 day'
  return `${diffDays} days`
}

export function formatDaysAgo (date: Date | string): string {
  const eventDate = typeof date === 'string' ? parseDate(date, 'yyyy-MM-dd') : new Date(date)
  const today = new Date()
  eventDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  const diffTime = today.getTime() - eventDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 1) return '1 day ago'
  return `${diffDays} days ago`
}

type DateFields = Pick<DeliveryData, 'deliveryDate' | 'deliveryTime'>

// Scheduled sends always go out at 7am in the recipient's chosen timezone;
// callers that render their own date string append the time the same way.
export function withDeliveryTime (dateStr: string, deliveryTime?: string | null) {
  return deliveryTime ? `${dateStr} @ 7am ${deliveryTime}` : dateStr
}

export function formatDeliveryDate ({ deliveryDate, deliveryTime }: DateFields) {
  const dateStr = isToday(deliveryDate) ? 'Today' : formatDate(deliveryDate, { format: 'E MMM d', showNow: true })

  return withDeliveryTime(dateStr, deliveryTime)
}

// `new Date('1990-08-03')` is parsed as UTC, so reading it back with the local
// getters lands on the previous day anywhere west of UTC.
export function parseLocalDate (value: string | Date): Date {
  if (typeof value !== 'string') return value

  const [year, month, day] = value.split('T')[0].split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function formatEventDate (dateStr: string | Date) {
  return dateFnsFormat(parseLocalDate(dateStr), 'MMM d')
}

export function formatDeliveryDateTime (dateTime: DeliveryData['deliverAt']) {
  if (!dateTime) return 'Now'

  const dateParsed = typeof dateTime === 'string' ? dateFnsParseISO(dateTime) : dateTime

  const dateStr = isToday(dateTime)
    ? `Today @ ${dateFnsFormat(dateParsed, 'haaa').toLocaleLowerCase()}`
    : `on ${dateFnsFormat(dateParsed, 'E, MMM d, yyyy')}`

  return dateStr
}

export function formatBirthday (
  date: DateStr | DateTimeStr | number | null | undefined,
  includeYear: boolean = false,
): string {
  return formatDate(date, {
    format: includeYear ? 'MMM d, yyyy' : 'MMM d',
  })
}
